// pages/spend.js — Spend page.
//
// Owns the `data-page="protection"` section. Renders the proxy-attributed
// LLM spend table (per-agent, per-repo, per-model rollup) plus
// total-cost/total-calls summary chips. Cloud-mode forks to
// renderCloudSpend.
//
// Migration note: depends on legacy.spendState (page-local mutable
// state for window/data/repoFilter), legacy.fetchSpend (async),
// legacy.formatUSD, legacy.isCloudMode, legacy.renderCloudSpend,
// legacy.escapeHTML, legacy.patchHTML.
//
// The window-picker `<select>` change handler and the .spend-repo-
// chip click handler are bound globally in dashboard.js. They both
// just re-call `renderSpendPage()` (which is now this module's
// render via the legacy dispatch + dashboard:tick). No handler
// migration needed in this step.
//
// Per Worktrees bug-sweep finding: no initial render in start() —
// legacy code handles activePage-change render. Module only listens
// for dashboard:tick to stay fresh.
import { state } from "../state.js";
import { spendState } from "../page-state.js";
import { patchHTML } from "../dom.js";
import { escapeHTML, formatUSD, formatDuration } from "../util.js";
import { isCloudMode } from "../ui.js";
import { withBuFilter } from "../bu-switcher.js";
import { api } from "../api.js";
import { parseAgentID, eventRowHTML, FLAGGED_EVENT_TYPES } from "../format.js";
import { eventsLinkAttrs, windowLabel } from "../event-links.js";
import { decryptHint } from "../crypto.js";
import { decryptEventRow } from "../decrypt-helpers.js";
import { fetchSpend, fetchValue } from "../fetchers.js";
import {
  loadPlanSelections,
  planMonthlyUSD,
  hasAnyPlanSelection,
} from "../page-state.js";
import { openPlanModal, bindPlanModal } from "./plan-modal.js";
import { renderRecommendations, cleanupDrawerAnimation } from "./recommendations.js";

const FLAGGED_PAGE_SIZE = 25;

// The flagged-event list under the Protection numbers: collisions,
// conflicts, preemptions, bypasses and expired leases, from the SAME place
// and window the numbers count — this computer's events (/v1/events) on the
// local dashboard, the team's incident records (/v1/teams/incidents) hosted —
// fetched a page at a time so every one is reachable. A Protection number
// can narrow it to its own types (spendState.flaggedOnly).
const flagged = { key: "", rows: null, total: 0, error: "" };

// unmanagedSince is when this computer started telling real unmanaged
// conflicts from harmless changes; the conflict count and list start there.
function unmanagedSince() {
  const v = spendState.value;
  return (v && v.conflicts_unmanaged_since) || "";
}

// localTypes adds that start time to the unmanaged types (?type=x@<ISO>).
export function localTypes(types, since) {
  return types.map((t) =>
    since && (t === "write.unmanaged_conflict" || t === "delete.unmanaged_conflict") ? t + "@" + since : t,
  );
}

export function flaggedURL(cloud, types, window, page, since, q) {
  const p = new URLSearchParams();
  p.set("since", window);
  p.set("type", (cloud ? types : localTypes(types, since)).join(","));
  if (q) p.set("q", q);
  p.set("limit", String(FLAGGED_PAGE_SIZE));
  p.set("offset", String(page * FLAGGED_PAGE_SIZE));
  return cloud ? withBuFilter("/v1/teams/incidents?" + p) : "/v1/events?" + p;
}

// incidentRow shapes a decrypted hosted incident like an event row, so the
// list renders and expands the same way in both modes.
function incidentRow(i) {
  return {
    EventType: i.event_type,
    event_type: i.event_type,
    AgentID: i.agent_id,
    agent_id: i.agent_id,
    OccurredAt: i.occurred_at,
    occurred_at: i.occurred_at,
    Resource: i.rel_path || "",
    resource: i.rel_path || "",
    id: i.lease_id,
    Payload: { purpose: i.requested_purpose || "", held_by: i.held_by_agent || "", lease_id: i.lease_id },
    payload: { purpose: i.requested_purpose || "", held_by: i.held_by_agent || "", lease_id: i.lease_id },
  };
}

async function loadFlagged(url, cloud) {
  try {
    const d = await api(url);
    if (flagged.key !== url) return;
    if (typeof d.total !== "number") throw new Error("the server did not say how many events match; update AgentCollision");
    flagged.rows = cloud
      ? await Promise.all((d.incidents || []).map(async (i) => incidentRow(await decryptIncident(i))))
      : await Promise.all((d.events || []).map(decryptEventRow));
    flagged.total = d.total;
    flagged.error = "";
    // A shorter window can leave the page past the end: go to the last page.
    const last = Math.max(0, Math.ceil(d.total / FLAGGED_PAGE_SIZE) - 1);
    if (spendState.flaggedPage > last) {
      spendState.flaggedPage = last;
      if (state.activePage === "protection") renderFlaggedFeed();
      return;
    }
  } catch (err) {
    if (flagged.key !== url) return;
    flagged.rows = null;
    flagged.error = (err && err.message) || String(err);
  }
  if (state.activePage === "protection") renderFlaggedFeed();
}

export function renderFlaggedFeed() {
  const body = document.getElementById("flagged-body");
  if (!body) return;
  const pager = document.getElementById("flagged-pagination");
  const head = document.getElementById("flagged-only");
  const cloud = isCloudMode();
  // The local conflict list waits for /v1/value, which says when this
  // computer's conflict counting starts.
  if (!cloud && !spendState.value) {
    patchHTML(body, `<div class="empty"><div class="empty-title">Loading flagged events…</div></div>`);
    return;
  }
  const only = spendState.flaggedOnly;
  const types = only ? only.types : [...FLAGGED_EVENT_TYPES];
  const q = (spendState.flaggedFilter || "").trim();
  const url = flaggedURL(cloud, types, spendState.window, spendState.flaggedPage, unmanagedSince(), q);
  if (flagged.key !== url) {
    flagged.key = url;
    flagged.rows = null;
    flagged.error = "";
    loadFlagged(url, cloud);
  }
  if (head) {
    head.hidden = !only;
    if (only)
      head.innerHTML =
        '<div class="ev-query-text"><span class="ev-query-label">Showing</span> <strong>' + escapeHTML(only.label) +
        "</strong></div>" + '<button type="button" class="btn btn-sm" data-flagged-clear="1">Show all flagged events</button>';
  }
  if (flagged.error) {
    patchHTML(body, `<div class="empty"><div class="empty-title">Could not load flagged events</div><div class="empty-detail">${escapeHTML(flagged.error)}</div></div>`);
    if (pager) patchHTML(pager, "");
    return;
  }
  if (flagged.rows === null) {
    patchHTML(body, `<div class="empty"><div class="empty-title">Loading flagged events…</div></div>`);
    if (pager) patchHTML(pager, "");
    return;
  }
  const rows = flagged.rows;
  if (rows.length === 0) {
    patchHTML(
      body,
      `<div class="empty"><div class="empty-title">No flagged events</div><div class="empty-detail">${
        q ? (cloud ? "Nothing matches. On the team view, search covers the event type and chat; file names are encrypted." : "Nothing matches.") : "Nothing flagged in this window."
      }</div></div>`,
    );
  } else {
    const start = spendState.flaggedPage * FLAGGED_PAGE_SIZE;
    patchHTML(body, rows.map((e, i) => eventRowHTML(e, false, start + i)).join(""));
  }
  if (!pager) return;
  const totalPages = Math.max(1, Math.ceil(flagged.total / FLAGGED_PAGE_SIZE));
  const start = spendState.flaggedPage * FLAGGED_PAGE_SIZE;
  const prevDis = spendState.flaggedPage === 0 ? "disabled" : "";
  const nextDis = spendState.flaggedPage >= totalPages - 1 ? "disabled" : "";
  const info = flagged.total
    ? `Showing <strong style="color: var(--text);">${start + 1}&ndash;${start + flagged.rows.length}</strong> of <strong style="color: var(--text);">${flagged.total.toLocaleString()}</strong> ${q ? "matching " : ""}flagged events`
    : "";
  patchHTML(
    pager,
    `<div class="info">${info}</div>
      <div class="controls">
        <button data-flagged-page="prev" ${prevDis}>&lsaquo; Prev</button>
        <span class="page-indicator">Page ${spendState.flaggedPage + 1} / ${totalPages}</span>
        <button data-flagged-page="next" ${nextDis}>Next &rsaquo;</button>
      </div>`,
  );
}

// showFlagged narrows the list to one Protection number's types and
// scrolls to it (hosted conflicts and handoffs live only in incidents).
export function showFlagged(only) {
  spendState.flaggedOnly = only;
  spendState.flaggedPage = 0;
  renderFlaggedFeed();
  const panel = document.getElementById("flagged-body");
  if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
}

// decryptIncident turns a hosted incident (encrypted hints) into the
// plaintext fields the Recent rows and the flagged list show.
async function decryptIncident(e) {
  const out = {
    event_type: e.event_type,
    agent_id: e.agent_id,
    occurred_at: e.occurred_at,
    duration_held_s: e.duration_held_s,
    // Cloud incidents reference by lease_id (no numeric event id exists
    // server-side); surfaced as the row's id.
    lease_id: e.lease_id,
    // task_priority arrives as a single tag from the coordinator; map it
    // to requested_task_priority so the chip renders (the daemon picks
    // the requester's priority first, see incidentPriority).
    requested_task_priority: e.task_priority,
  };
  // Decrypt the path hint → rel_path (what the row leads with).
  const path = await decryptHint(e.resource_hint);
  if (path) out.rel_path = path;
  // Decrypt the purpose blob → requested/held purposes (the narrative).
  const pj = await decryptHint(e.purpose_hint);
  if (pj) {
    try {
      const parsed = JSON.parse(pj);
      if (parsed.requested) out.requested_purpose = parsed.requested;
      if (parsed.held) out.held_by_purpose = parsed.held;
    } catch {
      /* not JSON — ignore */
    }
  }
  // Decrypt the counterparty blob → who held it / who was blocked.
  const cj = await decryptHint(e.counterparty_hint);
  if (cj) {
    try {
      const parsed = JSON.parse(cj);
      for (const k of [
        "held_by_agent",
        "requested_by",
        "preempted_agent",
        "by_agent",
      ]) {
        if (parsed[k]) out[k] = parsed[k];
      }
    } catch {
      /* ignore */
    }
  }
  return out;
}

// fetchCloudValue pulls /v1/teams/value (the cloud counterpart of /v1/value)
// and decrypts each incident's hints client-side into the SAME shape the
// local protectionExamplesHTML expects (rel_path, requested_purpose,
// held_by_purpose, held_by_agent, …), so the hosted band renders identically.
// The coordinator only ever stored the encrypted hints — decryption happens
// here with the team HMAC key. Best-effort: returns null on any failure so
// the band just doesn't render (never blocks the spend table).
async function fetchCloudValue(window) {
  let v;
  try {
    v = await api(
      withBuFilter(
        "/v1/teams/value?since=" + encodeURIComponent(window) + "&limit=50",
      ),
    );
  } catch {
    return null;
  }
  if (!v) return null;
  const incidents = await Promise.all((v.incidents || []).map(decryptIncident));
  return {
    collisions_prevented: v.collisions_prevented || 0,
    conflicts_caught: v.conflicts_caught || 0,
    preemptions: v.preemptions || 0,
    edits_coordinated: v.edits_coordinated || 0,
    coordinated_autonomously: v.coordinated_autonomously || 0,
    incidents,
    incidents_total: v.incidents_total || incidents.length,
  };
}

// renderCloudSpend is the cloud-mode variant of renderSpendPage —
// pulls /v1/teams/spend?since=<window> from the coordinator (which
// aggregates per-machine hourly rollups) and renders a
// machine/agent/model rollup table. Replaces local fetchSpend path
// when isCloudMode() is true.
async function renderCloudSpend() {
  const body = document.getElementById("spend-body");
  if (!body) return;
  const sel = document.getElementById("spend-window");
  const window = (sel && sel.value) || "24h";
  // Server-side pagination (identical to the local path: one page via
  // limit/offset; the server returns total_rows + window-wide totals) AND
  // the team value band in parallel. The value band (decrypted client-side)
  // gives hosted parity with local's "AgentCollision protected you" section.
  const pageSize = spendState.pageSize || 25;
  const offset = (spendState.page || 0) * pageSize;
  // Server-side search (same ?q= the local path uses) — filters across all
  // rows so the hosted total_rows + page agree.
  const qStr = (spendState.filterText || "").trim();
  const search = qStr ? "&q=" + encodeURIComponent(qStr) : "";
  let data, cloudValue;
  try {
    [data, cloudValue] = await Promise.all([
      api(
        withBuFilter(
          "/v1/teams/spend?since=" +
            encodeURIComponent(window) +
            "&limit=" +
            pageSize +
            "&offset=" +
            offset +
            search,
        ),
      ),
      fetchCloudValue(window),
    ]);
  } catch (e) {
    patchHTML(
      body,
      '<div class="empty"><div class="empty-title">Could not load team spend</div><div class="empty-detail">' +
        escapeHTML(e.message || "fetch failed") +
        "</div></div>",
    );
    return;
  }
  // Persist the response so the shared pager handler (bindSpendPager) can read
  // total_rows/limit — same state slot the local view uses, so paging is
  // uniform across modes. If a shrinking window left us past the last page,
  // snap back and refetch the now-valid offset once.
  spendState.dataByChat = data;
  if (clampSpendPage(data)) {
    const off2 = (spendState.page || 0) * pageSize;
    try {
      data = await api(
        withBuFilter(
          "/v1/teams/spend?since=" +
            encodeURIComponent(window) +
            "&limit=" +
            pageSize +
            "&offset=" +
            off2 +
            search,
        ),
      );
      spendState.dataByChat = data;
    } catch {
      /* keep the prior page on refetch failure */
    }
  }
  const rows = (data && data.rows) || [];
  // Feed the cloud value payload into the same state slot the local hero
  // reads, so spendHeroHTML renders the protected-you band identically.
  spendState.value = cloudValue;
  const meteredProviders = meteredProvidersFrom(rows);

  // Keep the legacy summary chips (if present) in sync.
  const totalCostEl = document.getElementById("spend-total-cost");
  const totalCallsEl = document.getElementById("spend-total-calls");
  if (totalCostEl) totalCostEl.textContent = formatUSD(data.total_cost_usd || 0);
  if (totalCallsEl)
    totalCallsEl.textContent = (data.total_calls || 0).toLocaleString();

  // Hosted now renders the SAME layout as local: the Usage + protected-you
  // hero bands, then the per-chat table, then the pager. (The coordinator's
  // /v1/teams/spend now returns the per-chat group=chat shape, so spendTotals
  // and perChatTableHTML work on it unchanged.)
  if (rows.length === 0) {
    patchHTML(
      body,
      spendHeroHTML(spendTotals(data), cloudValue, meteredProviders, window) +
        `<div class="empty" style="opacity:1;animation:none;">
        <div class="empty-title">No spend recorded for this window</div>
        <div class="empty-detail" style="text-align: left; max-width: 580px; margin: 12px auto 0;">
          <p style="margin-bottom: 12px;">Spend is captured two ways:</p>
          <ul style="padding-left: 22px; line-height: 1.7;">
            <li><strong>Local session logs</strong> (works on Pro/Max subscriptions) &mdash; the daemon reads your <code>~/.claude/projects</code> session files automatically. Token counts are exact; dollar figures are an <em>API-equivalent estimate</em>, since a subscription is flat-billed, not per-token.</li>
            <li><strong>Spend proxy</strong> (other API-key agents, off by default) &mdash; turn on <strong>Run the spend proxy</strong> in Settings, then point the agent's <code>ANTHROPIC_BASE_URL</code> at <code>http://127.0.0.1:11434</code>. Calls are metered in real time and can enforce spend caps. Claude Code never needs it.</li>
          </ul>
          <p style="margin-top: 12px; font-size: 11px; color: var(--text-muted);">If this stays empty: confirm Claude Code has run on this machine (so <code>~/.claude/projects</code> exists), try a wider window, or check the daemon log for <code>jsonl spend</code>. Opt out with <code>AC_NO_JSONL_SPEND=1</code>.</p>
        </div>
      </div>`,
    );
    afterHero(meteredProviders);
    return;
  }

  let html = spendHeroHTML(
    spendTotals(data),
    cloudValue,
    meteredProviders,
    window,
  );
  // TEMP: per-chat usage table + pager hidden (Claude Code usage records are a
  // distraction on the Protection page). Restore the two lines below to bring
  // them back.
  // html += perChatTableHTML(rows);
  // html += spendPagerHTML(data);
  void rows;
  patchHTML(body, html);
  afterHero(meteredProviders);
}

// renderByChatView fetches /v1/spend?group=chat and renders the
// per-chat rollup. One row per agent_id summed across all models;
// answers "which chats spent the most?" without the user having
// to mentally aggregate the per-model breakdown.
// fetchLiveNegotiations pulls in-flight negotiations (/v1/negotiations) and
// attaches them to spendState.value so the Protection band's "Live coordination"
// section renders. Best-effort — a failure just omits the section.
async function fetchLiveNegotiations() {
  try {
    const r = await api("/v1/negotiations");
    if (r && Array.isArray(r.negotiations) && spendState.value) {
      spendState.value.liveNegotiations = r.negotiations;
    }
  } catch {
    /* live section just doesn't render */
  }
}

async function renderByChatView() {
  // Fetch the per-chat rollup AND the value payload so the hero's
  // coordination-impact band renders alongside the spend table.
  await Promise.all([fetchSpend(), fetchValue()]);
  await fetchLiveNegotiations();
  // If a shrinking row set left us past the last page, snap back and
  // refetch the now-valid offset once before rendering.
  if (clampSpendPage(spendState.dataByChat)) {
    await fetchSpend();
  }
  const body = document.getElementById("spend-body");
  if (!body) return;

  if (spendState.error) {
    renderSpendError(body);
    return;
  }

  const data = spendState.dataByChat || {};
  const allRows = data.rows || [];
  const repos = data.repos || [];
  const totalCost = data.total_cost_usd || 0;
  const meteredProviders = meteredProvidersFrom(allRows);

  const totalCostEl = document.getElementById("spend-total-cost");
  const totalCallsEl = document.getElementById("spend-total-calls");
  const windowDisplayEl = document.getElementById("spend-window-display");
  if (totalCostEl) totalCostEl.textContent = formatUSD(totalCost);
  if (totalCallsEl)
    totalCallsEl.textContent = (data.total_calls || 0).toLocaleString();
  if (windowDisplayEl) windowDisplayEl.textContent = spendState.window;

  if (allRows.length === 0) {
    patchHTML(
      body,
      spendHeroHTML(spendTotals(data), spendState.value, meteredProviders, spendState.window) +
        spendEmptyHTML(),
    );
    afterHero(meteredProviders);
    return;
  }

  // Apply repo filter (same semantics as "by model" view — include
  // unattributed rows so they don't vanish when a filter is on).
  const rows = spendState.repoFilter
    ? allRows.filter(
        (r) => r.repo_root === spendState.repoFilter || !r.repo_root,
      )
    : allRows;

  // Hero first (value vs pay vs savings + impact + plan picker), then
  // the per-chat table.
  let html = spendHeroHTML(
    spendTotals(data),
    spendState.value,
    meteredProviders,
    spendState.window,
  );
  // Repo chips (same component as the by-model view).
  // TEMP: repo chips + the per-chat usage table + pager are hidden along with
  // the Usage band — they're the Claude Code usage "records" that distract from
  // the protection story. Restore by re-adding the three lines below.
  // if (repos.length > 0) { html += renderRepoChips(repos); }
  // html += perChatTableHTML(rows);
  // html += spendPagerHTML(data);
  void repos; void rows;
  patchHTML(body, html);
  afterHero(meteredProviders);
}

// perChatTableHTML renders the per-chat spend table body (Chat / Models /
// Calls / Tokens / Active / Cost). Shared by the local (renderByChatView)
// and hosted (renderCloudSpend) paths so both render the IDENTICAL table —
// the rows just come from /v1/spend?group=chat locally vs /v1/teams/spend
// (now also group=chat) on the coordinator.
function perChatTableHTML(rows) {
  let html = `<table class="spend-table">
    <thead><tr>
      <th>Chat</th>
      <th>Models</th>
      <th class="num">Calls</th>
      <th class="num">Tokens</th>
      <th class="num">Active</th>
      <th class="num">Cost</th>
    </tr></thead><tbody>`;
  for (const r of rows) {
    html += `<tr>
      <td>${chatCellHTML(r)}</td>
      <td>${modelChipsHTML(r.models_used)}</td>
      <td class="num">${(r.calls || 0).toLocaleString()}</td>
      <td class="num spend-dim">${((r.input_tokens || 0) + (r.output_tokens || 0)).toLocaleString()}</td>
      <td class="num spend-dim" title="${escapeHTML((r.first_call || "") + " → " + (r.last_call || ""))}">${formatDuration(r.active_duration_s || 0)}</td>
      <td class="num spend-cost">${formatUSD(r.cost_usd || 0)}</td>
    </tr>`;
  }
  if (rows.length === 0) {
    html += `<tr><td colspan="6" class="spend-table-empty">No spend in the selected repo for this window.</td></tr>`;
  }
  html += "</tbody></table>";
  return html;
}

// chatCellHTML renders the Chat column: a tool badge + a readable short
// id (the repo, when known, sits under it as muted context). Far more
// scannable than a raw "claude-code/2be6e4c5…" string.
function chatCellHTML(r) {
  if (r.agent_id === "(unknown)") {
    return `<span class="spend-dim" title="No agent id on the request">(unknown)</span>`;
  }
  const parsed = parseAgentID(r.agent_id || "");
  const tool = parsed.tool || "agent";
  const short = (parsed.shortID || "").replace("???", "") || "—";
  const fullId = r.agent_id || "";
  const repo = r.repo_root
    ? `<div class="spend-chat-repo" title="${escapeHTML(r.repo_root)}">${escapeHTML(
        r.repo_root.split("/").slice(-1)[0] || r.repo_root,
      )}</div>`
    : "";
  // The short id stays the scannable label; clicking it copies the FULL
  // unique chat id (agent_id) and the tooltip shows it in full, so a
  // specific chat can be referenced without a wall of UUID in the table.
  return `<div class="spend-chat" title="${escapeHTML(fullId)}">
    <div class="spend-chat-top"><span class="spend-tool-badge">${escapeHTML(tool)}</span><code class="spend-chat-id spend-chat-id-copy" data-copy="${escapeHTML(fullId)}" data-tooltip="${escapeHTML(fullId)} — click to copy">${escapeHTML(short)}</code></div>
    ${repo}
  </div>`;
}

// modelChipsHTML renders the model chips, shortening long versioned ids
// (claude-haiku-4-5-20251001 → haiku-4-5) so the column stays compact.
function modelChipsHTML(models) {
  return (models || [])
    .map((m) => {
      const short = String(m)
        .replace(/^claude-/, "")
        .replace(/-\d{8}$/, "");
      return `<span class="spend-model-chip" title="${escapeHTML(m)}">${escapeHTML(short)}</span>`;
    })
    .join("");
}



// spendPagerHTML renders the server-side pager under a spend table. The
// table shows one page (the server already applied LIMIT/OFFSET); this
// reads the window-wide totals the server returns alongside it:
//   total_rows — distinct grouped rows across the whole window
//   limit      — page size the server honored
//   offset     — current offset (page * limit)
// Mirrors the Events pager UX (First/Prev/indicator/Next/Last) and reuses
// the shared .pagination CSS. Renders nothing for a single page so it
// doesn't clutter small windows. Buttons carry data-spend-page; the
// delegated handler in this module turns them into spendState.page moves.
function spendPagerHTML(data) {
  const total = (data && data.total_rows) || 0;
  const limit = (data && data.limit) || spendState.pageSize || 25;
  const offset = (data && data.offset) || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return "";
  const page = Math.floor(offset / limit); // 0-based, server-derived
  const start = offset;
  const end = Math.min(offset + limit, total);
  const prevDisabled = page <= 0 ? "disabled" : "";
  const nextDisabled = page >= totalPages - 1 ? "disabled" : "";
  return `<div class="pagination">
    <div class="info">
      Showing <strong style="color: var(--text);">${(start + 1).toLocaleString()}&ndash;${end.toLocaleString()}</strong>
      of <strong style="color: var(--text);">${total.toLocaleString()}</strong> rows
    </div>
    <div class="controls">
      <button data-spend-page="first" ${prevDisabled}>&laquo; First</button>
      <button data-spend-page="prev"  ${prevDisabled}>&lsaquo; Prev</button>
      <span class="page-indicator">Page ${page + 1} / ${totalPages}</span>
      <button data-spend-page="next"  ${nextDisabled}>Next &rsaquo;</button>
      <button data-spend-page="last"  ${nextDisabled}>Last &raquo;</button>
    </div>
  </div>`;
}

// clampSpendPage guards against a stale page offset stranding the user on
// an empty table. The page-resetting handlers (window/view/repo) cover
// the common cases, but live data can shrink total_rows between ticks
// while the user sits on a high page; the server then returns 0 rows for
// an offset past the end. If we detect that (offset >= total_rows > 0),
// snap to the last valid page. Returns true if it changed the page, so
// the caller knows to refetch before rendering. Pure arithmetic on the
// response the server just sent — no extra round-trip to discover it.
function clampSpendPage(data) {
  const total = (data && data.total_rows) || 0;
  const limit = (data && data.limit) || spendState.pageSize || 25;
  if (total <= 0) return false;
  const lastPage = Math.max(0, Math.ceil(total / limit) - 1);
  if ((spendState.page || 0) > lastPage) {
    spendState.page = lastPage;
    return true;
  }
  return false;
}

// renderRepoChips emits the repo filter chip row. Pulled out so
// both views share one renderer — the only meaningful difference
// between the two table layouts was the per-row repo column, not
// the filter UI.
function renderRepoChips(repos) {
  let html =
    '<div style="margin-bottom: 12px; display: flex; gap: 6px; flex-wrap: wrap; align-items: center;">';
  html +=
    '<span style="font-size: 11px; color: var(--text-muted); text-transform: uppercase;">Repo</span>';
  const chip = (label, value, active) => {
    const bg = active ? "var(--brand, #2563eb)" : "var(--bg-soft, #f3f4f6)";
    const fg = active ? "#fff" : "var(--text, #111)";
    return `<button class="spend-repo-chip" data-repo="${escapeHTML(value)}" style="padding: 3px 10px; font-size: 12px; border-radius: 999px; border: 1px solid var(--border, #ddd); background: ${bg}; color: ${fg}; cursor: pointer;">${escapeHTML(label)}</button>`;
  };
  html += chip("All", "", spendState.repoFilter === "");
  for (const r of repos) {
    const short = r.split("/").slice(-2).join("/") || r;
    html += chip(short, r, spendState.repoFilter === r);
  }
  html +=
    '<span style="font-size: 11px; color: var(--text-muted); margin-left: 8px;" title="Spend is attributed to each agent\'s most recent repo (registry-based). For agents that span multiple repos in the window, current repo wins.">attribution: best-effort (current)</span>';
  html += "</div>";
  return html;
}

// renderSpendError + spendEmptyHTML are split out so the by-chat
// view can reuse the same UX as the by-model view without
// duplicating the 503-detection logic.
function renderSpendError(body) {
  const isUnconfigured =
    (spendState.error || "").toLowerCase().includes("spend_not_configured") ||
    (spendState.error || "").toLowerCase().includes("not wired");
  if (isUnconfigured) {
    patchHTML(
      body,
      `<div class="empty">
        <div class="empty-title">Spend tracking not configured</div>
        <div class="empty-detail">
          The daemon couldn't open a spend ledger (likely running with <code>--ephemeral</code>).<br/>
          Restart the daemon with a persistent SQLite DB to enable.
        </div>
      </div>`,
    );
  } else {
    patchHTML(
      body,
      '<div class="empty"><div class="empty-title">Could not load spend</div><div class="empty-detail">' +
        escapeHTML(spendState.error) +
        "</div></div>",
    );
  }
  const totalCostEl = document.getElementById("spend-total-cost");
  const totalCallsEl = document.getElementById("spend-total-calls");
  if (totalCostEl) totalCostEl.textContent = "$0.00";
  if (totalCallsEl) totalCallsEl.textContent = "0";
}

function spendEmptyHTML() {
  return `<div class="empty">
    <div class="empty-title">No spend recorded yet</div>
    <div class="empty-detail" style="text-align: left; max-width: 600px; margin: 12px auto 0;">
      <p style="margin: 0 0 10px;">Claude Code spend is read from its own session logs on this computer (<code>~/.claude/projects</code>), for API keys and Pro/Max plans alike. It appears here within a minute of a chat making a call.</p>
      <p style="margin: 0;">Still empty after a chat has run? Check <code>agc verify</code>. Other API-key agents can use the spend proxy, which is off by default: turn on <strong>Run the spend proxy</strong> in Settings (or <code>agc config set spend_proxy_enabled true</code>), restart AgentCollision, then point the agent's <code>ANTHROPIC_BASE_URL</code> at <code>http://127.0.0.1:11434</code>.</p>
    </div>
  </div>`;
}


// spendHeroHTML renders the Spend page hero as a row of compact stat
// cells (matching the Overview page's .hero-stats-grid look — muted,
// small uppercase labels, no equation, no neon band). It anchors the
// big "API-equivalent value" number against what the user actually pays
// (from the plan modal) so the figure reads as a saving, not a bill.
//
//   apiValueUSD       — windowed API-equivalent spend (the ledger total)
//   value             — /v1/value payload (impact metrics), may be null
//   meteredProviders  — provider keys present in the ledger this window
//   win               — the active window label ("24h"/"7d"/"30d")
// spendHeroHTML renders the hero as two restrained bands of compact
// stat cells (Overview style):
//   1. Usage — REAL facts only: calls, tokens, sessions for the window,
//      plus the user's actual plan cost as a flat reference. NO
//      "API-equivalent value", NO "you save", NO multiplier — those read
//      as invented savings theater (the user rejected them).
//   2. AgentCollision protected you — REAL logged events only: counts of
//      collisions prevented / conflicts caught / edits coordinated. NO
//      dollar figure (coordination value isn't honestly quantifiable in
//      $ — a prevented collision is a counterfactual worth $0..hours).
//      Concrete recent example rows are appended by the caller.
//
//   totals          — { cost, calls, input, output, sessions } for the window
//   value           — /v1/value payload (real event counts), may be null
//   meteredProviders — provider keys present in the ledger this window
//   win             — the active window label ("24h"/"7d"/"30d")
// spendTotals rolls a /v1/spend response (either view) into the totals
// the hero needs: cost, calls, input/output tokens, and distinct
// sessions (rows in the by-chat view are per-session; for by-model we
// count distinct agent_ids).
function spendTotals(data) {
  const rows = (data && data.rows) || [];
  let input = 0,
    output = 0;
  const agents = new Set();
  for (const r of rows) {
    input += r.input_tokens || 0;
    output += r.output_tokens || 0;
    if (r.agent_id) agents.add(r.agent_id);
  }
  return {
    cost: (data && data.total_cost_usd) || 0,
    calls: (data && data.total_calls) || 0,
    input,
    output,
    // by-chat: one row per chat → rows.length is the session count.
    // by-model: distinct agent_ids.
    sessions: data && data.group === "chat" ? rows.length : agents.size,
  };
}

// Incident examples now arrive inside the /v1/value payload
// (spendState.value.incidents) — fetched server-side by type + window so
// they reconcile with the counts. No separate fetch needed.

// INCIDENT_META maps an incident event type to its display label, a
// colored dot variant, and a plain-English one-liner. Drives the compact
// example rows under the "protected you" band.
const INCIDENT_META = {
  "lease.blocked": {
    label: "Collision prevented",
    dot: "danger",
    why: "an agent tried to edit a file another was holding",
  },
  "lease.preempted": {
    label: "Preempted",
    dot: "warn",
    why: "higher-priority work took over a held file",
  },
  "write.unmanaged_conflict": {
    label: "Unmanaged write caught",
    dot: "danger",
    why: "something outside AgentCollision changed a file another chat held, or one in a folder several chats share",
  },
  "delete.unmanaged_conflict": {
    label: "Unmanaged delete caught",
    dot: "danger",
    why: "something outside AgentCollision deleted a file another chat held, or one in a folder several chats share",
  },
  "commit.bypass_detected": {
    label: "Commit bypass caught",
    dot: "warn",
    why: "a commit touched a file a peer was holding, outside coordination",
  },
  "branch.contended": {
    label: "Branch contention flagged",
    dot: "warn",
    why: "two agents were working the same git branch — flagged to coordinate",
  },
  "lease.negotiation_opened": {
    label: "Negotiation opened",
    dot: "muted",
    why: "a blocked agent opened a negotiation with the holder",
  },
  "lease.negotiation_granted": {
    label: "Coordinated autonomously",
    dot: "good",
    why: "an idle holder's file was handed off to a waiting agent — no human needed",
  },
  "lease.negotiation_countered": {
    label: "Counter-offered",
    dot: "muted",
    why: "the holder was active, so they asked the requester to wait while they finish",
  },
  "lease.negotiation_accepted": {
    label: "Wait accepted",
    dot: "muted",
    why: "the requester agreed to wait for the holder to hand off",
  },
  "lease.negotiation_resolved": {
    label: "Coordinated autonomously",
    dot: "good",
    why: "a full negotiation completed — holder finished, then handed the file off to the waiting agent",
  },
};

// prettyFile turns a raw resource/rel_path into a readable filename:
// strips the "file:<repo>:" prefix and any temp-file suffix, keeps the
// last two path segments so it's identifiable but not a wall of text.
function prettyFile(raw) {
  let p = String(raw || "");
  const colon = p.lastIndexOf(":");
  if (colon > 4) p = p.slice(colon + 1); // drop file:<abs-repo>: prefix
  // strip a trailing temp suffix like ".tmp.90780.0a91c39…"
  p = p.replace(/\.tmp\.[0-9a-f.]+$/i, "");
  const parts = p.split("/").filter(Boolean);
  return parts.slice(-2).join("/") || p || "—";
}

// relTime renders a compact relative time ("3d ago", "2h ago"). Falls
// back to the date when older than a week.
function relTime(iso) {
  const then = Date.parse(iso || "");
  if (!then) return "";
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 90) return "just now";
  if (secs < 3600) return Math.round(secs / 60) + "m ago";
  if (secs < 86400) return Math.round(secs / 3600) + "h ago";
  if (secs < 7 * 86400) return Math.round(secs / 86400) + "d ago";
  return new Date(then).toISOString().slice(0, 10);
}

// shortAgent renders a compact agent label ("claude-code/2be6e4c5") from
// a raw agent id, for the cross-agent detail line.
function shortAgent(id) {
  if (!id) return "";
  const p = parseAgentID(id);
  const sid = (p.shortID || "").replace("???", "");
  return p.tool + (sid ? "/" + sid : "");
}

// purposePhrase trims a raw lease purpose to a short, readable clause for
// the collision narrative. Purposes are free-text ("fix the login bug on
// auth.ts"); we keep them short so the row stays scannable.
function purposePhrase(raw) {
  let p = String(raw || "").trim();
  if (!p) return "";
  // Drop a leading path-ish token if the purpose is just the file again.
  if (p.length > 64) p = p.slice(0, 61).replace(/\s+\S*$/, "") + "…";
  return p;
}

// heldDuration renders the holder's hold time ("held 3m", "held 2h 10m")
// from duration_held_s. Empty when unknown or zero.
function heldDuration(secs) {
  const n = Number(secs);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 60) return "held " + Math.round(n) + "s";
  if (n < 3600) return "held " + Math.floor(n / 60) + "m";
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  return "held " + (m > 0 ? h + "h " + m + "m" : h + "h");
}

// priorityTag renders a small priority chip ("P1") from a raw tag, or "".
function priorityTag(raw) {
  const p = String(raw || "")
    .trim()
    .toUpperCase();
  if (!p) return "";
  // Normalize "p1"/"P1"/"priority-1" → "P1" when it looks like one.
  const m = p.match(/P?\s*([0-9])/);
  return m ? "P" + m[1] : p.length <= 6 ? p : "";
}

// collisionNarrative builds the "learn from it" sentence for an incident:
// what the requester wanted vs. what the holder was already doing. Falls
// back to the static meta.why when purposes aren't present. The whole point
// of PR3 — turns "blocked" into a story the user can act on.
function collisionNarrative(e, meta) {
  const reqPurpose = purposePhrase(e.requested_purpose);
  const heldPurpose = purposePhrase(e.held_by_purpose);
  const holder = shortAgent(e.held_by_agent);
  const requester = shortAgent(e.requested_by) || shortAgent(e.agent_id);
  // Preempt / unmanaged variants read differently; only lease.blocked has
  // the clean "A wanted X while B was doing Y" shape.
  if (e.event_type === "lease.blocked" && (reqPurpose || heldPurpose)) {
    const left = requester
      ? `<span class="spend-ix-agent">${escapeHTML(requester)}</span>${reqPurpose ? " wanted to " + escapeHTML(reqPurpose) : " tried to edit it"}`
      : reqPurpose
        ? "an agent wanted to " + escapeHTML(reqPurpose)
        : "an agent tried to edit it";
    const right = heldPurpose
      ? `${holder ? `<span class="spend-ix-agent">${escapeHTML(holder)}</span>` : "another agent"} was already ${escapeHTML(heldPurpose)}`
      : holder
        ? `<span class="spend-ix-agent">${escapeHTML(holder)}</span> was holding it`
        : "another agent was holding it";
    return `${left}; ${right}`;
  }
  // Non-blocked types: enrich the static reason with the counterpart agent.
  const other =
    holder ||
    shortAgent(e.preempted_agent) ||
    shortAgent(e.by_agent) ||
    requester;
  return other
    ? `${meta.why} <span class="spend-ix-agent">${escapeHTML(other)}</span>`
    : meta.why;
}

// protectionExamplesHTML renders the recent incident examples from the
// /v1/value payload (server-fetched by type + window, so they reconcile
// with the counts). Each row leads with the readable filename, a colored
// type tag, a plain-English reason ENRICHED with the cross-agent detail
// (who held it / who got preempted), and a relative time. Footer says
// "showing N of M" + links to the full Events page so it's clearly a
// sample, not all there is.
// incidentDetailHTML builds the expanded full writeup shown when a collision
// row is clicked: every present field on its own labeled line, plus a deep-link
// to the matching event on the Events page. Kept to fields we actually have so
// a sparse incident doesn't render empty rows.
function incidentDetailHTML(e, refId) {
  const rows = [];
  const add = (label, val) => {
    if (val === undefined || val === null || val === "") return;
    rows.push(
      `<div class="spend-ix-kv"><span class="spend-ix-k">${escapeHTML(label)}</span><span class="spend-ix-v">${escapeHTML(String(val))}</span></div>`,
    );
  };
  // The auto-acquire boilerplate purpose ("auto-acquired via PreToolUse on …")
  // is noise in the writeup — only show a purpose when it's a real stated one.
  const meaningful = (p) =>
    p && !String(p).startsWith("auto-acquired") ? p : "";
  add("Requested by", e.requested_by || e.by_agent || e.agent_id);
  add("Wanted to", meaningful(e.requested_purpose));
  add("Held by", e.held_by_agent || e.preempted_agent);
  add("Was doing", meaningful(e.held_by_purpose));
  add("Outcome", e.outcome);
  const dur = heldDuration(e.duration_held_s);
  add("Held for", dur ? dur.replace(/^held /, "") : "");
  const reqP = priorityTag(e.requested_task_priority);
  const heldP = priorityTag(e.held_task_priority);
  if (reqP || heldP) add("Priority", [reqP, heldP].filter(Boolean).join(" vs "));
  add("Tool", e.tool);
  add("Intent", e.intent);
  add("Worktree", e.worktree_id);
  add("Lease", e.lease_id);
  add("Fencing token", e.fencing_token);
  add("Verified human", e.actor_email);
  if (e.occurred_at) {
    let abs = e.occurred_at;
    try {
      abs = new Date(e.occurred_at).toLocaleString();
    } catch {
      /* keep raw */
    }
    add("When", abs);
  }
  // Deep-link to this exact event on the Events page (pre-filters by id). Only
  // when we have a numeric event id (the local /v1/value path); lease-id-only
  // cloud incidents fall back to no link.
  const eventLink =
    e.event_id != null && e.event_id !== ""
      ? `<a href="#" class="spend-ix-eventlink" data-page-link="events" data-event-id="${escapeHTML(String(e.event_id))}">view this event in Events →</a>`
      : "";
  return `<div class="spend-ix-expand">${rows.join("")}${eventLink}</div>`;
}

// Number of Recent rows shown before the "Show all" expander. Keeps the
// band compact by default while letting the user pull every incident the
// payload carries in-place (Fix: see-all-without-leaving-Protection).
const PROTECTION_INLINE_CAP = 4;

function protectionExamplesHTML(value) {
  const ex = (value && value.incidents) || [];
  const total = (value && value.incidents_total) || ex.length;
  if (ex.length === 0) return "";
  // "Show all" reveals every incident PRESENT in the payload inline; until
  // then we cap the visible rows so the band stays compact. The cap only
  // bites when the payload actually carries more than it.
  const showAll = !!spendState.showAllIncidents;
  const visible = showAll ? ex : ex.slice(0, PROTECTION_INLINE_CAP);
  const rows = visible
    .map((e) => {
      const meta = INCIDENT_META[e.event_type] || {
        label: e.event_type,
        dot: "muted",
        why: "",
      };
      const file = prettyFile(e.rel_path || e.resource);
      const fullPath = (e.rel_path || e.resource || "").replace(/^file:[^:]*:/, "");
      // The narrative: what each agent was trying to do. This is the
      // "learn from it" line the user asked for.
      const why = collisionNarrative(e, meta);
      // Detail chips: tool/IDE, how long it was held, the priority stakes,
      // and the verified human (when an OIDC-authed actor is on the event).
      // Each is best-effort — only rendered when the data is present, so a
      // sparse event stays clean.
      const chips = [];
      if (e.tool)
        chips.push(
          `<span class="spend-ix-chip">${escapeHTML(String(e.tool))}</span>`,
        );
      const dur = heldDuration(e.duration_held_s);
      if (dur) chips.push(`<span class="spend-ix-chip">${escapeHTML(dur)}</span>`);
      const reqP = priorityTag(e.requested_task_priority);
      const heldP = priorityTag(e.held_task_priority);
      if (reqP && heldP && reqP !== heldP)
        chips.push(
          `<span class="spend-ix-chip spend-ix-chip-pri" title="requested ${escapeHTML(reqP)} vs held ${escapeHTML(heldP)}">${escapeHTML(reqP)} vs ${escapeHTML(heldP)}</span>`,
        );
      else if (reqP || heldP)
        chips.push(
          `<span class="spend-ix-chip spend-ix-chip-pri">${escapeHTML(reqP || heldP)}</span>`,
        );
      if (e.actor_email)
        chips.push(
          `<span class="spend-ix-chip spend-ix-chip-actor" title="verified human">${escapeHTML(String(e.actor_email))}</span>`,
        );
      const detail = chips.length
        ? `<div class="spend-ix-detail">${chips.join("")}</div>`
        : "";
      // A visible, copyable reference id for the incident: the numeric event
      // id locally (/v1/value), or the lease_id on hosted (/v1/teams/value,
      // which has no numeric id). Lets a specific collision be found/cited.
      const refId = e.event_id != null && e.event_id !== "" ? "#" + e.event_id : e.lease_id || "";
      const idChip = refId
        ? `<span class="spend-ix-id" data-copy="${escapeHTML(String(refId))}" data-tooltip="${escapeHTML(String(refId))} — click to copy">${escapeHTML(String(refId))}</span>`
        : "";
      // The row is clickable to expand a full writeup. Key it by the reference
      // id (event_id or lease_id) so the expanded set survives re-renders.
      // Cloud incidents from /v1/teams/value can lack BOTH a numeric event id
      // and a lease_id; without a key the row wasn't clickable on hosted. Fall
      // back to a DETERMINISTIC, PER-ROW-UNIQUE key (NOT random/time-based —
      // those change every re-render and orphan the expanded-set tracking) so
      // each row expands INDEPENDENTLY. The fallback is content + the row's
      // index in the FULL `ex` array (not the sliced `visible`): content alone
      // collides when two incidents share type+path+time (e.g. two
      // branch.contended on the same branch in the same second) — clicking one
      // would expand both. Indexing `ex` (via indexOf) keeps the key stable
      // across the show-all toggle while making every row unique.
      const ixKey = String(
        e.event_id != null && e.event_id !== ""
          ? e.event_id
          : e.lease_id ||
              "ix-" + ex.indexOf(e) + "-" + (e.event_type || "") + "-" +
                (e.rel_path || e.resource || e.occurred_at || ""),
      );
      const isExpanded =
        spendState.expandedIncidents && spendState.expandedIncidents.has(ixKey);
      const expandBlock = isExpanded ? incidentDetailHTML(e, refId) : "";
      // Chevron affordance: a subtle ▸/▾ that signals the row expands (paired
      // with the cursor:pointer + hover styling on .spend-ix-clickable).
      const chevron = `<span class="spend-ix-chev" aria-hidden="true">${isExpanded ? "&#9662;" : "&#9656;"}</span>`;
      return `<div class="spend-ix-row spend-ix-clickable${isExpanded ? " spend-ix-open" : ""}" data-ix-id="${escapeHTML(ixKey)}">
        <span class="spend-ix-dot spend-ix-${meta.dot}"></span>
        <div class="spend-ix-main">
          <div class="spend-ix-top">
            ${chevron}
            <code class="spend-ix-file" title="${escapeHTML(fullPath)}">${escapeHTML(file)}</code>
            <span class="spend-ix-kind spend-ix-${meta.dot}">${escapeHTML(meta.label)}</span>
            ${idChip}
          </div>
          <div class="spend-ix-why">${why}</div>
          ${detail}
          ${expandBlock}
        </div>
        <span class="spend-ix-time">${escapeHTML(relTime(e.occurred_at))}</span>
      </div>`;
    })
    .join("");
  // Inline "Show all" toggle — lets the user see every incident the payload
  // carries WITHOUT leaving the Protection page. Only shown when the payload
  // actually holds more than the inline cap. The server caps the example pool
  // (6 today, no param to raise it), so when incidents_total exceeds what the
  // payload returned we keep the honest "view all in Events" deep-link for the
  // rest — labeled so the user knows this band shows a sample of the total.
  const moreInPayload = ex.length > PROTECTION_INLINE_CAP;
  const serverCapped = total > ex.length;
  const toggle = moreInPayload
    ? showAll
      ? `<button type="button" class="spend-ex-toggle" data-ix-showall="collapse">Show fewer</button>`
      : `<button type="button" class="spend-ex-toggle" data-ix-showall="expand">Show all ${ex.length} &rarr;</button>`
    : "";
  // The lead label: how many of the payload are visible vs how many exist in
  // total (when the server returned fewer than the count).
  const countLabel = serverCapped
    ? `Showing ${visible.length} of ${total.toLocaleString()}`
    : showAll || !moreInPayload
      ? `Showing all ${ex.length}`
      : `Showing ${visible.length} of ${ex.length}`;
  const eventsLink = `<a href="#" data-page-link="events" class="spend-ex-link">view all in Events &rarr;</a>`;
  const footer = `<div class="spend-ex-foot">${countLabel}${toggle ? " · " + toggle : ""} · ${eventsLink}</div>`;
  return `<div class="spend-ex">
    <div class="spend-ex-head">Recent</div>
    ${rows}
    ${footer}
  </div>`;
}

// protectedYouBandHTML renders the "AgentCollision protected you" band — the
// core "here's how AgentCollision is protecting you" story. It ALWAYS renders,
// in every state, because protection is always active even when the count is
// zero or the data hasn't synced yet (hosted). A missing/null value is treated
// as all-zero. Shared by the local (/v1/value) and cloud (/v1/teams/value)
// paths so both surfaces show an identical, always-present band.
//
// Three body states under the always-shown stat cells:
//   - has incident examples → the colored Recent list (protectionExamplesHTML)
//   - counts but no examples → just the cells (e.g. hosted synced counts, no
//     decrypted detail) — the cells carry the story
//   - everything zero → a calm "watching, ready to step in" line that frames
//     zero as active protection, not absence.
export const PROTECTION_TYPES = {
  collisions: ["lease.blocked"],
  conflicts: ["write.unmanaged_conflict", "delete.unmanaged_conflict", "commit.bypass_detected", "branch.contended"],
  edits: ["lease.acquired"],
  coordinated: ["lease.negotiation_granted", "lease.negotiation_resolved"],
};

// protectionLinks makes each Protection number open the events it counts,
// from the same place: this computer's events locally; hosted, the team's
// event log for collisions and edits and its incident records (the flagged
// list below) for conflicts and handoffs.
export function protectionLinks(value, window, cloud) {
  const win = windowLabel(window);
  const label = {
    collisions: "Collisions prevented",
    conflicts: "Conflicts caught",
    edits: "Edits coordinated",
    coordinated: "Coordinated autonomously",
  };
  const since = (value && value.conflicts_unmanaged_since) || "";
  const out = {};
  for (const k of Object.keys(PROTECTION_TYPES)) {
    const l = label[k] + " · " + win;
    if (cloud && (k === "conflicts" || k === "coordinated")) {
      out[k] = ' data-flagged-types="' + PROTECTION_TYPES[k].join(",") + '" data-flagged-label="' + escapeHTML(l) + '" role="link" tabindex="0"';
    } else if (cloud) {
      out[k] = eventsLinkAttrs({ types: PROTECTION_TYPES[k], since: window, label: l });
    } else {
      out[k] = eventsLinkAttrs({ types: localTypes(PROTECTION_TYPES[k], since), since: window, label: l, source: "local" });
    }
  }
  return out;
}

function protectedYouBandHTML(value, win) {
  const collisions = (value && value.collisions_prevented) || 0;
  const conflicts = (value && value.conflicts_caught) || 0;
  const edits = (value && value.edits_coordinated) || 0;
  const coordinated = (value && value.coordinated_autonomously) || 0;
  const links = protectionLinks(value, spendState.window, isCloudMode());
  const examples = protectionExamplesHTML(value);
  const anything = collisions > 0 || conflicts > 0 || edits > 0 || coordinated > 0;

  // Body under the cells: Recent examples when present, otherwise the
  // reassuring "protection is active" note (used both at true zero and when
  // counts exist but detail hasn't synced).
  const body = examples
    ? examples
    : `<div class="spend-band-note">${
        anything
          ? "AgentCollision is coordinating your agents in the background — every edit ran under a lease, conflict-free."
          : "No collisions yet this window — AgentCollision is watching every file your agents touch and steps in the moment two reach for the same one."
      }</div>`;

  return `<div class="spend-band spend-band-protect">
    <div class="spend-band-head">AgentCollision protected you<span class="spend-band-win">${escapeHTML(win)}</span></div>
    <div class="hero-stats-grid spend-stats">
      ${statCell("Collisions prevented", collisions.toLocaleString(), "an agent backed off a held file", "", "An agent tried to edit a file another agent was actively holding a lease on. AgentCollision blocked the write so they couldn't silently overwrite each other.", links.collisions)}
      ${statCell("Conflicts caught", conflicts.toLocaleString(), "files, commits & branches flagged", "", "Coordination conflicts caught BEYOND a single file lease: an unmanaged write/delete on a held file, a commit that bypassed a peer's lease, or two agents working the same git branch. Flagged so you can check before they clash.", links.conflicts)}
      ${statCell("Edits coordinated", edits.toLocaleString(), "ran under a lease, conflict-free", "", "Edits that ran cleanly under a lease — no conflict, because AgentCollision serialized access to the file.", links.edits)}
      ${statCell("Coordinated autonomously", coordinated.toLocaleString(), "agents handed off on their own", "accent-good", "Negotiations the daemons resolved without you: a blocked agent asked for a file, an idle holder's lease was auto-handed-off. Agents coordinating end-to-end, no human in the loop.", links.coordinated)}
    </div>
    ${liveNegotiationsHTML(value)}
    ${body}
  </div>`;
}

// liveNegotiationsHTML renders the in-flight negotiations (agents brokering a
// held file right now) — the "live coordination" view that makes Protection the
// single screen for agent-to-agent governance. Empty → nothing rendered (the
// Recent list + counts already tell the resolved story).
function liveNegotiationsHTML(value) {
  const negs = (value && value.liveNegotiations) || [];
  if (!negs.length) return "";
  const stateLabel = {
    countered: "holder is finishing — requester waiting",
    accepted: "requester agreed to wait — holder will hand off",
    resolved: "handed off",
  };
  const rows = negs
    .map((n) => {
      const who = shortAgent(n.requested_by) || n.requested_by || "an agent";
      const holder = shortAgent(n.held_by_agent) || n.held_by_agent || "the holder";
      const file = prettyFile(n.file_path || n.resource);
      const st = stateLabel[n.state] || n.state || "open";
      return `<div class="spend-neg-row">
        <span class="spend-neg-dot"></span>
        <div class="spend-neg-main">
          <div class="spend-neg-top"><code class="spend-ix-file">${escapeHTML(file)}</code><span class="spend-neg-state">${escapeHTML(st)}</span></div>
          <div class="spend-neg-who">${escapeHTML(who)} ⇄ ${escapeHTML(holder)}</div>
        </div>
      </div>`;
    })
    .join("");
  return `<div class="spend-neg-live">
    <div class="spend-neg-head">Live coordination<span class="spend-neg-count">${negs.length} in flight</span></div>
    ${rows}
  </div>`;
}

function spendHeroHTML(totals, value, meteredProviders, win) {
  const t = totals || {};
  const calls = t.calls || 0;
  const tokens = (t.input || 0) + (t.output || 0);
  const sessions = t.sessions || 0;
  const havePlan = hasAnyPlanSelection(meteredProviders);
  const monthlyPlan = planMonthlyUSD(meteredProviders);

  // Plan reference cell: the user's real flat plan, or a CTA to set it.
  const planCell = havePlan
    ? statCell(
        "Your plan",
        `<span class="spend-pay-chip" role="button" tabindex="0">${formatUSD(monthlyPlan)}/mo</span>`,
        "flat subscription · edit",
        "small",
      )
    : statCell(
        "Your plan",
        `<button class="spend-set-plan link-btn">Set plan</button>`,
        "what you pay /mo",
        "muted",
      );

  // Band 1 — real usage. Dollars here are honest: actual metered token
  // cost when the proxy is wired; otherwise this is the local-log
  // API-equivalent, labelled plainly as a reference, not a bill.
  const usageBand = `<div class="spend-band spend-band-usage">
    <div class="spend-band-head">Usage<span class="spend-band-win">${escapeHTML(win)}</span></div>
    <div class="hero-stats-grid spend-stats">
      ${statCell("Calls", calls.toLocaleString(), `${tokens.toLocaleString()} tokens`, "")}
      ${statCell("Sessions", sessions ? sessions.toLocaleString() : "—", "parallel chats", "")}
      ${planCell}
    </div>
    <div class="spend-assume">Token counts are exact; the per-chat cost is an <em>estimate</em> at API rates — your subscription is flat-billed, not per-token.</div>
  </div>`;

  // Band 2 — AgentCollision protected you. Real event counts only.
  const acBand = protectedYouBandHTML(value, win);

  // Inline nudge when the user skipped the plan modal — quiet, not a re-pop.
  const nudge =
    !havePlan && spendState._planSkipped
      ? `<div class="spend-nudge"><button class="spend-set-plan link-btn">Set your plan</button> to show what you pay alongside your usage.</div>`
      : "";

  // TEMP (hide Claude Code usage records — distracting on the Protection page):
  // the Usage band (calls/tokens/cost/plan) + the plan nudge are commented out
  // for now, leaving just the protection/collision band. Restore by putting
  // ${usageBand}${nudge} back into the spend-hero below.
  void usageBand;
  void nudge;
  return `<div class="spend-hero">${acBand}</div>`;
}

// statCell builds one Overview-style stat cell. `value` may be HTML
// (e.g. a button/chip). `variant` tweaks the number color.
function statCell(label, value, sub, variant, tip, link) {
  const cls =
    variant === "accent"
      ? "value accent"
      : variant === "accent-good"
        ? "value spend-good"
        : variant === "muted"
          ? "value spend-muted"
          : variant === "small"
            ? "value spend-small"
            : "value";
  // Optional hover tooltip on the label (the global installTooltip wires any
  // [data-tooltip]). A faint cue on the label signals it's hoverable.
  const labelAttrs = tip
    ? ` class="label stat-label-tip" data-tooltip="${escapeHTML(tip)}"`
    : ` class="label"`;
  return `<div class="hero-stat-cell${link ? " kpi-link" : ""}"${link || ""}>
    <div${labelAttrs}>${label}</div>
    <div class="${cls}">${value}</div>
    ${sub ? `<div class="delta">${sub}</div>` : ""}
  </div>`;
}

// maybeOpenPlanModalFirstVisit pops the plan modal the first time the
// user lands on Spend with metered spend but no plan set (and they
// haven't skipped this session). Re-render happens via the modal's
// onSaved callback.
function maybeOpenPlanModalFirstVisit(meteredProviders) {
  if (spendState._planModalShown) return;
  // Wait for the server hydrate to be ATTEMPTED before deciding to prompt.
  // Without this, a user whose plan lives on the account (server) but not in
  // THIS browser's cache — a new login / new browser / cleared cookies — gets
  // re-prompted in the split second before loadPlanSelections' async server
  // fetch resolves. Once the server has been consulted, hasAnyPlanSelection
  // reflects the account value and this gate is honest. (loadPlanSelections
  // re-renders on resolve, so this runs again with the flag set.)
  if (!spendState._planServerChecked) return;
  if (hasAnyPlanSelection(meteredProviders)) return;
  if (spendState._planSkipped) return;
  if (!meteredProviders || meteredProviders.length === 0) return;
  spendState._planModalShown = true;
  openPlanModal(meteredProviders, () => renderSpendPage());
}

// bindPlanChips wires the "Set your plan" buttons + the "$X/mo · edit"
// chip to open the modal. Delegated off the spend section, bound once.
let planChipsBound = false;
function bindPlanChips(meteredProviders) {
  // Capture the latest providers for the handler.
  spendState._lastMeteredProviders = meteredProviders;
  if (planChipsBound) return;
  const section = document.querySelector('section.page[data-page="protection"]');
  if (!section) return;
  planChipsBound = true;
  const open = () =>
    openPlanModal(spendState._lastMeteredProviders || meteredProviders, () =>
      renderSpendPage(),
    );
  section.addEventListener("click", (e) => {
    if (e.target.closest(".spend-set-plan") || e.target.closest(".spend-pay-chip")) {
      e.preventDefault();
      open();
    }
  });
  section.addEventListener("keydown", (e) => {
    if (
      (e.key === "Enter" || e.key === " ") &&
      e.target.classList &&
      e.target.classList.contains("spend-pay-chip")
    ) {
      e.preventDefault();
      open();
    }
  });
}

// afterHero runs the post-render wiring shared by every render path:
// bind the plan chips/buttons and pop the first-visit modal when
// appropriate. Replaces the old bindPlanPicker() call.
function afterHero(meteredProviders) {
  bindPlanChips(meteredProviders);
  maybeOpenPlanModalFirstVisit(meteredProviders);
}

// meteredProvidersFrom returns the distinct provider keys present in a
// set of spend rows — drives which plan pickers appear (ledger-driven).
function meteredProvidersFrom(rows) {
  const set = new Set();
  for (const r of rows || []) {
    if (r.provider) set.add(r.provider);
    // by-chat rows don't carry provider; infer from models_used.
    for (const m of r.models_used || []) {
      const lm = String(m).toLowerCase();
      if (lm.startsWith("claude")) set.add("anthropic");
      else if (lm.startsWith("gpt") || lm.startsWith("o1") || lm.startsWith("o3"))
        set.add("openai");
      else if (lm.startsWith("gemini")) set.add("gemini");
    }
  }
  return [...set];
}

export async function renderSpendPage() {
  if (isCloudMode()) {
    await renderCloudSpend();
    // The flagged feed + recommendations read state.events / spendState.value,
    // both already populated in cloud mode — render them after the spend body.
    renderFlaggedFeed();
    renderRecommendations();
    return;
  }
  // Pull current window from the select if it exists. (The window-change
  // handler in policy-handlers.js owns resetting spendState.page to 0 on
  // an actual change; this is just the cold-render hydration.)
  const sel = document.getElementById("spend-window");
  if (sel && sel.value) spendState.window = sel.value;

  // One view: the per-chat rollup ("which chats are driving cost?"). The
  // old By chat / By model toggle was removed — it was dead in cloud mode
  // and split the surface for no real benefit. By chat is the single view.
  await renderByChatView();
  renderFlaggedFeed();
  renderRecommendations();
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  // Hydrate the user's saved plan selections before the first render so the
  // hero can show "you pay / you save" immediately. Server-first (account-
  // level, migration 059) with a localStorage cache for instant paint; the
  // callback re-renders once the async server value lands (or the fetch
  // fails), which also releases the first-visit modal gate.
  loadPlanSelections(() => {
    if (state.activePage === "protection") renderSpendPage();
  });
  // Bind the server-side pager (First/Prev/Next/Last under the table).
  bindSpendPager();
  // Wire the plan modal once (open/save/skip handlers).
  bindPlanModal();
  // Initial render — cloud mode has no daemon-driven tick loop, so
  // without this the page sits on its skeleton until manual nav.
  // See policy.js / worktrees.js for the same fix.
  renderSpendPage();
  tickHandler = () => {
    // The page's data-page/activePage is "protection" (the Spend page was
    // renamed; "spend" is only a legacy URL alias). The old "spend" check here
    // never matched, so tick re-renders silently no-op'd on this page.
    if (state.activePage === "protection") renderSpendPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

// bindSpendPager wires the server-side pager controls (data-spend-page)
// under the spend table. Delegated off the stable section so it survives
// the body's patchHTML re-renders; guarded so it binds once. Each click
// derives totalPages from the last response's total_rows/limit, moves
// spendState.page, and re-renders (which re-fetches the new offset via
// fetchSpend → the server returns that page). Mirrors the Events pager
// handler in bootstrap.js, but offset-driven instead of client-slice.
let spendPagerBound = false;
function bindSpendPager() {
  if (spendPagerBound) return;
  const section = document.querySelector('section.page[data-page="protection"]');
  if (!section) return;
  spendPagerBound = true;
  section.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-spend-page]");
    if (!btn || btn.disabled) return;
    // Derive page bounds from the live per-chat response (the one view).
    const data = spendState.dataByChat || {};
    const total = data.total_rows || 0;
    const limit = data.limit || spendState.pageSize || 25;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const cur = spendState.page || 0;
    switch (btn.dataset.spendPage) {
      case "first":
        spendState.page = 0;
        break;
      case "prev":
        spendState.page = Math.max(0, cur - 1);
        break;
      case "next":
        spendState.page = Math.min(totalPages - 1, cur + 1);
        break;
      case "last":
        spendState.page = totalPages - 1;
        break;
    }
    renderSpendPage();
  });
}

function stop() {
  if (!started) return;
  started = false;
  // Reset the "show all incidents" expansion on page exit so returning to
  // Protection starts collapsed (the default). The flag lives on shared
  // spendState and otherwise persists across navigation + poll-tick re-renders.
  spendState.showAllIncidents = false;
  // Cancel any in-flight reco-drawer open/close animation timer so it doesn't
  // fire after we've left the page.
  cleanupDrawerAnimation();
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="protection"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}

// Re-export renderCloudSpend so bootstrap.js's bridge entry
// stays valid. The bridge resolves it via object shorthand; without
// the export, AC_LEGACY.renderCloudSpend would be undefined.
export { renderCloudSpend };

